import { LAYOUT_KIND_CHART } from './dashboardLayoutItems';
import { colorSchemeForDimension } from './reportingChartUtils';
import { DATA_SOURCE_TARGET_TENANT } from './functionSqlTemplates';

export { DATA_SOURCE_TARGET_TENANT };

const GRID_COLS = 12;
const CHART_W = 4;
const CHART_H = 10;

/** @typedef {{ functionName: string, title: string, vizType: string, dimensionFilter?: string, w?: number, h?: number, description?: string, statDisplay?: string }} DefaultChartDef */
/** @typedef {'tenant'} DataSourceTarget */
/** @typedef {{ title: string, description?: string, dataSourceTarget?: DataSourceTarget, charts: DefaultChartDef[] }} DefaultSectionDef */

export const DEFAULT_DASHBOARD_GROUP_PREFIX = 'Integration';

export const DEFAULT_DASHBOARD_SECTIONS = [
    {
        title: 'Upload batches',
        description: 'File upload history, batch status, and processing job metrics (tenant database).',
        dataSourceTarget: DATA_SOURCE_TARGET_TENANT,
        charts: [
            {
                functionName: 'dv_upload_batch_count',
                title: 'Upload batches',
                vizType: 'stat',
                w: 3,
                h: 6,
                statDisplay: 'total',
            },
            {
                functionName: 'dv_upload_batch_record_totals',
                dimensionFilter: 'total_records',
                title: 'Total records',
                vizType: 'stat',
                w: 3,
                h: 6,
                statDisplay: 'total',
            },
            {
                functionName: 'dv_upload_batch_record_totals',
                dimensionFilter: 'processed_records',
                title: 'Processed records',
                vizType: 'stat',
                w: 3,
                h: 6,
                statDisplay: 'total',
            },
            {
                functionName: 'dv_upload_batch_record_totals',
                dimensionFilter: 'failed_records',
                title: 'Failed records',
                vizType: 'stat',
                w: 3,
                h: 6,
                statDisplay: 'total',
            },
            {
                functionName: 'dv_upload_batch_record_totals',
                dimensionFilter: 'skipped_records',
                title: 'Skipped records',
                vizType: 'stat',
                w: 3,
                h: 6,
                statDisplay: 'total',
            },
            { functionName: 'dv_upload_batch_summary', dimensionFilter: 'status', title: 'Batch status', vizType: 'pie' },
            { functionName: 'dv_upload_batch_summary', dimensionFilter: 'batch_type', title: 'Batch type', vizType: 'donut' },
            { functionName: 'dv_upload_batch_summary', dimensionFilter: 'file_format', title: 'File format', vizType: 'bar_horizontal' },
            { functionName: 'dv_upload_batch_summary', dimensionFilter: 'format_valid', title: 'Format validation', vizType: 'pie' },
            { functionName: 'dv_upload_batch_daily_volume', title: 'Daily upload volume', vizType: 'line' },
            {
                functionName: 'dv_processing_job_record_totals',
                dimensionFilter: 'jobs',
                title: 'Processing jobs',
                vizType: 'stat',
                w: 3,
                h: 6,
                statDisplay: 'total',
            },
            {
                functionName: 'dv_processing_job_record_totals',
                dimensionFilter: 'records_successful',
                title: 'Job records successful',
                vizType: 'stat',
                w: 3,
                h: 6,
                statDisplay: 'total',
            },
            {
                functionName: 'dv_processing_job_record_totals',
                dimensionFilter: 'records_failed',
                title: 'Job records failed',
                vizType: 'stat',
                w: 3,
                h: 6,
                statDisplay: 'total',
            },
            { functionName: 'dv_processing_job_summary', dimensionFilter: 'status', title: 'Job status', vizType: 'pie' },
            { functionName: 'dv_processing_job_summary', dimensionFilter: 'job_type', title: 'Job type', vizType: 'bar_horizontal' },
            { functionName: 'dv_processing_job_summary', dimensionFilter: 'row_outcome', title: 'Ingest duplicates & outcomes', vizType: 'bar_horizontal' },
            { functionName: 'dv_processing_job_daily_volume', title: 'Daily job volume', vizType: 'line' },
        ],
    },
];

function newChartId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `chart-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function buildVizSpec(vizType, encodingOverride, colorScheme, statDisplay) {
    const isTimeSeries = vizType === 'line' || vizType === 'area' || vizType === 'scatter';
    const isStat = vizType === 'stat';
    const encoding = encodingOverride ?? (isTimeSeries
        ? { category: 'period', value: 'record_count' }
        : isStat
            ? { category: 'category', value: 'total_count' }
            : { category: 'category', value: 'total_count' });
    return {
        type: vizType,
        mode: 'chart',
        encoding,
        ...(isStat ? { statDisplay: statDisplay ?? encodingOverride?.statDisplay ?? 'auto' } : {}),
        ...(colorScheme ? { colorScheme } : {}),
    };
}

/**
 * Resolve registered function GUID by schema + function name (optionally scoped to a data source).
 * @param {object[]} registeredFunctions
 * @param {string} functionName
 * @param {string} [schema]
 * @param {string|null} [dataSourceId]
 */
export function resolveFunctionId(registeredFunctions, functionName, schema = 'public', dataSourceId = null) {
    const candidates = registeredFunctions.filter(f => {
        const name = f.functionName ?? f.FunctionName;
        const sch = f.schema ?? f.Schema ?? 'public';
        return name === functionName && sch === schema;
    });
    if (candidates.length === 0) return null;
    if (dataSourceId) {
        const match = candidates.find(f => (f.dataSourceId ?? f.DataSourceId) === dataSourceId);
        if (match) return match.id ?? match.Id ?? null;
    }
    return candidates[0]?.id ?? candidates[0]?.Id ?? null;
}

/**
 * @param {{ tenant?: string }} [dataSourceIds]
 */
export function buildSectionDashboardLayout(registeredFunctions, section, dataSourceIds = {}) {
    const dataSourceId = dataSourceIds.tenant;
    const warnings = [];
    const missing = [];
    const charts = [];
    let gridY = 0;
    let col = 0;
    let rowMaxH = 0;

    for (const def of section.charts) {
        const functionId = resolveFunctionId(registeredFunctions, def.functionName, 'public', dataSourceId ?? null);
        if (!functionId) {
            missing.push(def.functionName);
            warnings.push(`${section.title}: function not registered — ${def.functionName}`);
            continue;
        }

        const w = def.w ?? (def.vizType === 'stat' ? 3 : CHART_W);
        const h = def.h ?? (def.vizType === 'stat' ? 6 : CHART_H);
        if (col + w > GRID_COLS) {
            gridY += rowMaxH || CHART_H;
            col = 0;
            rowMaxH = 0;
        }

        charts.push({
            id: newChartId(),
            kind: LAYOUT_KIND_CHART,
            title: def.title,
            description: def.description ?? null,
            functionId,
            functionRef: { schema: 'public', functionName: def.functionName },
            datasetKey: 'default',
            dimensionFilter: def.dimensionFilter ?? null,
            viz: buildVizSpec(
                def.vizType,
                def.encoding,
                def.colorScheme ?? colorSchemeForDimension(def.dimensionFilter),
                def.statDisplay,
            ),
            layout: { x: col, y: gridY, w, h },
        });

        col += w;
        rowMaxH = Math.max(rowMaxH, h);
    }

    return { layoutJson: { charts }, warnings, missing };
}

/**
 * One dashboard per section.
 * @returns {{ dashboards: Array<{ name: string, description: string, layoutJson: object }>, warnings: string[], missing: string[] }}
 */
export function buildDefaultDashboards(registeredFunctions, sections = DEFAULT_DASHBOARD_SECTIONS, dataSourceIds = {}) {
    const allWarnings = [];
    const allMissing = [];
    const dashboards = [];

    for (const section of sections) {
        const { layoutJson, warnings, missing } = buildSectionDashboardLayout(
            registeredFunctions,
            section,
            dataSourceIds,
        );
        allWarnings.push(...warnings);
        allMissing.push(...missing);

        if ((layoutJson.charts ?? []).length === 0) continue;

        dashboards.push({
            name: section.title,
            description: section.description ?? `Reporting charts: ${section.title}`,
            layoutJson,
        });
    }

    return { dashboards, warnings: allWarnings, missing: [...new Set(allMissing)] };
}

/** @deprecated Use buildDefaultDashboards — kept for tests */
export function buildDefaultDashboardLayout(registeredFunctions, sections = DEFAULT_DASHBOARD_SECTIONS) {
    const { dashboards, warnings, missing } = buildDefaultDashboards(registeredFunctions, sections);
    const charts = dashboards.flatMap(d => d.layoutJson?.charts ?? []);
    return { layoutJson: { charts }, warnings, missing };
}

/** All unique function names required by the default dashboard (for deploy/register). */
export function collectRequiredFunctionNames(sections = DEFAULT_DASHBOARD_SECTIONS) {
    const names = new Set();
    for (const section of sections) {
        for (const chart of section.charts) {
            names.add(chart.functionName);
        }
    }
    return [...names];
}
